import React, { useEffect, useRef } from 'react';
import * as echarts from 'echarts';
import InfoIcon from '@mui/icons-material/Info';
import { GaugeCardChart, GaugeCardContainer, GaugeCardNextScore, GaugeCardScore, GaugeCardTitle, GaugeCardTotal } from './gaugeCard.styles';
import { COLORS } from './colors';
import { Tooltip } from '@mui/material';

interface GaugeCardProps {
  title: string;
  tooltip: string;
  total: string | number | null | undefined;
  score: number | undefined;
  nextScore: string | undefined;
}

const GaugeCard: React.FC<GaugeCardProps> = ({ title, tooltip, total, score, nextScore }) => {
  const chartContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (score && chartContainerRef.current) {
      const chartData = () => {
        const DATA = [];

        for (let i = 0; i < score; i++) {
          let COLOR;

          switch (i) {
            case 0:
              COLOR = COLORS.brand[20];
              break;
            case 1:
              COLOR = COLORS.brand[40];
              break;
            case 2:
              COLOR = COLORS.brand[60];
              break;
            case 3:
              COLOR = COLORS.brand[80];
              break;
            case 4:
            default:
              COLOR = COLORS.brand[100];
              break;
          }

          DATA.push({
            value: i + 1,
            itemStyle: {
              color: COLOR,
            },
          });
        }

        return DATA;
      };
      const chartOptions = {
        series: [
          {
            type: 'gauge',
            min: 0,
            max: 5,
            startAngle: 180,
            endAngle: 0,
            data: chartData(),
            progress: {
              show: true,
              width: 30,
              overlap: true,
            },
            pointer: {
              show: false,
            },
            axisLine: {
              show: false,
            },
            axisLabel: {
              show: false,
            },
            splitLine: {
              show: false,
            },
            axisTick: {
              show: false,
            },
            detail: {
              show: false,
            },
          },
        ],
      };

      const gaugeChart = echarts.init(chartContainerRef.current);
      gaugeChart.setOption(chartOptions);

      const handleResize = () => {
        gaugeChart.resize();
      };
      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('resize', handleResize);
        gaugeChart.dispose();
      };
    }
  }, [score]);

  return (
    <GaugeCardContainer hasNextScore={Boolean(nextScore)}>
      <GaugeCardTitle>
        {title}
        <Tooltip title={<span style={{ whiteSpace: 'pre-line' }} dangerouslySetInnerHTML={{ __html: tooltip }} />}>
          <InfoIcon style={{ height: 16, width: 16, marginLeft: 4 }} />
        </Tooltip>
      </GaugeCardTitle>
      <GaugeCardChart ref={chartContainerRef} />
      <GaugeCardScore>{score}</GaugeCardScore>
      <GaugeCardTotal>{total}</GaugeCardTotal>
      {nextScore && <GaugeCardNextScore>{nextScore}</GaugeCardNextScore>}
    </GaugeCardContainer>
  );
};

export default GaugeCard;
